import { i18n, MessageDescriptor } from '@lingui/core'
import { msg, t } from '@lingui/core/macro'
import _ from 'lodash'
import { api } from 'api'
import { store } from 'redux/store'
import { sanitizeHtmlAndSvg } from './sanitize'

const storeState = store.getState()

// Replacing font icons with inline svg to prevent a poor user experience when the icon is not cached and takes a long time to load
// SVGs from https://fonts.google.com/icons

const ROOT_TEMPLATE = `
    <div>
        <span class="validation-tooltip-modal-root">
            <i class="<%- fn.getSuccessTextClass(result.json_body.summary.success) %> material-icons material-icons-font md-theme-default">
                <%- fn.getSuccessIcon(result.json_body.summary.success) %>
            </i>
            <span class="link-status-summary-text"><%- result.json_body.summary.optionSuccesses.length %>/<%- result.json_body.summary.totalNumOptions %> checks passed</span>
            <i
                class="material-icons material-icons-font link-refresh"
                style="font-size: 20px !important; position:absolute; top: 50%; right: 4px; margin-left: 15px; margin-top: -10px; cursor: pointer;"
                data-url="<%- linkValue %>"
                data-field-id="<%- linkGovernance.field_id %>"
                data-row-num="<%- rowNum %>"
            >
                refresh
            </i>
        </span>
        <div class="statusWrapper tooltipWrapper">
            <div class="statusPopover validationTooltip arrow-top">
                <h3><%- fn.getTooltipHeader(result.json_body.summary) %></h3>

                <p><%= fn.getTooltipDescription(result.json_body.summary) %></p>

                <ul class="validation-check-list">
                <% _.forEach(result.json_body.validationResults, function(validationResult) { %>

                    <% _.forEach(validationResult.checks, function(option) { %>

                    <li>
                    <i
                        class="<%- fn.getSuccessTextClass(option.success) %> material-icons material-icons-font md-theme-default"
                        title="<%- fn.getSuccessMessage(option.success) %>">
                        <%- fn.getSuccessIcon(option.success) %>
                    </i>
                        <%- fn.getOptionName(option) %>
                        </li>

                    <% }); %>

                <% }); %>
                </ul>
            </div>
        </div>
    </div>
`

const PENDING_TEMPLATE = `
<div>
    <span>
      <i class="material-icons md-theme-default text-secondary svg-icon-spin">
        <svg xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 0 24 24" width="24px" fill="#777">
          <path d="M13 2.05v3.03c3.39.49 6 3.39 6 6.92 0 .9-.18 1.75-.48 2.54l2.6 1.53c.56-1.24.88-2.62.88-4.07 0-5.18-3.95-9.45-9-9.95zM12 19c-3.87 0-7-3.13-7-7 0-3.53 2.61-6.43 6-6.92V2.05c-5.06.5-9 4.76-9 9.95 0 5.52 4.47 10 9.99 10 3.31 0 6.24-1.61 8.06-4.09l-2.6-1.53C16.17 17.98 14.21 19 12 19z"/>
        </svg>
      </i>
    </span>
    <div class="statusWrapper tooltipWrapper">
        <div class="statusPopover validationTooltip arrow-top">
            <h3>Validating Page...</h3>
            <p>We're currently validating your page. Hang tight for just a few seconds while we gather the
                results...</p>
        </div>
    </div>
</div>
`

const ERROR_TEMPLATE = `
 <div style="position:relative; padding-right: 32px; word-break:break-word;">
    <i class="material-icons material-icons-font md-theme-default text-danger" style="font-size: 20px !important;">warning</i>
    <span style="font-size:12px; color: rgba(0,0,0,.35); margin-right: 24px;">
        Link validation temporarily unavailable.
    </span>
    <i
        class="material-icons material-icons-font link-refresh"
        style="font-size: 20px !important; position:absolute; top: 50%; right: 4px; margin-top: -12px; cursor: pointer;"
        data-url="<%- linkValue %>"
        data-field-id="<%- linkGovernance.field_id %>"
        data-row-num="<%- rowNum %>"
    >
        refresh
    </i>
</div>
`

const TEMPLATE_403 = `
 <div style="position:relative; padding-right: 32px; word-break:break-word;">
    <i class="material-icons material-icons-font md-theme-default text-danger" style="font-size: 20px !important;">warning</i>
    <span style="font-size:12px; color: rgba(0,0,0,.35); margin-right: 24px;">
        Link validation blocked.
    </span>
    <p>Claravine Link Validators have been blocked by the servers responding to the request. Please reach out to your Claravine CSM on steps to take to whitelist Claravine link validators so that your links can be successfully validated.</p>
</div>
`

const NO_DATA_TEMPLATE = `
<div>
    <span>
        Link verification status was not recorded
    </span>
</div>
`

const COMPILED_TEMPLATE = _.template(ROOT_TEMPLATE)
const COMPILED_PENDING_TEMPLATE = _.template(PENDING_TEMPLATE)
const COMPILED_NO_DATA_TEMPLATE = _.template(NO_DATA_TEMPLATE)
const COMPILED_ERROR_TEMPLATE = _.template(ERROR_TEMPLATE)
const COMPILED_403_MESSAGE = _.template(TEMPLATE_403)

interface TypeURLAudits {
  json_body: Record<string, string>
  status: number
  error: string
}

export function renderV2LinkValidation(
  td: HTMLTableColElement,
  urlAudits: TypeURLAudits[] = [],
  linkValue: string,
  linkGovernance: { field_id: string },
  rowNum: number,
) {
  if (urlAudits.length === 0 && window.location.pathname.includes('reports')) {
    renderToTd(td, COMPILED_NO_DATA_TEMPLATE())
    return
  }

  // changing this to render the compiled error template to the give the user the ability to retry validation
  if (isTemporaryError(urlAudits)) {
    renderToTd(
      td,
      COMPILED_ERROR_TEMPLATE({
        linkValue,
        linkGovernance,
        rowNum,
      }),
    )
    return
  }

  // if blank, then nothing to render
  // nothing to rendr shouldn't be a pending mode though. todo: fix this so that nothing to render shows if it's not actually pending.
  if (urlAudits.length === 0 || isPendingOrInProgress(urlAudits)) {
    renderToTd(
      td,
      COMPILED_PENDING_TEMPLATE({
        result: urlAudits[0],
      }),
    )
    return
  }

  if (is403(urlAudits)) {
    renderToTd(td, COMPILED_403_MESSAGE())
    return
  }

  if (
    !urlAudits[0]?.json_body?.summary &&
    (urlAudits[0]?.status === 3 || urlAudits[0]?.status === 4)
  ) {
    renderToTd(
      td,
      COMPILED_ERROR_TEMPLATE({
        linkValue,
        linkGovernance,
        rowNum,
      }),
    )
    return
  }

  //  render the template
  const result = COMPILED_TEMPLATE({
    result: urlAudits[0],
    linkValue,
    linkGovernance,
    rowNum,
    fn: {
      getSuccessMessage,
      getSuccessIcon,
      getSuccessTextClass,
      getValidationName,
      getOptionName,
      getTooltipHeader,
      getTooltipDescription,
    },
  })

  renderToTd(td, result)
}

function is403(urlAudits) {
  if (urlAudits[0]?.json_body?.rawResponse?.status == 403) {
    return true
  }
  return false
}

function isPendingOrInProgress(urlAudits: TypeURLAudits[]) {
  return urlAudits.length === 1 && (urlAudits[0]?.status === 1 || urlAudits[0]?.status === 2)
}

function isTemporaryError(urlAudits: TypeURLAudits[]) {
  const [auditUrl] = urlAudits

  if (!auditUrl) return false

  const hasError = urlAudits.length === 1 && auditUrl.error
  const errorMessage = auditUrl.json_body.errorMessage || ''

  // todo: switch these to error codes instead of pivoting on the message
  const exceededLimit = urlAudits.length === 1 && errorMessage.includes('limit exceeded')
  const noResources =
    urlAudits.length === 1 && errorMessage.includes('available validation resource')

  return hasError && (exceededLimit || noResources)
}

function getTooltipHeader(resultSummary: { success: boolean }) {
  return resultSummary.success ? t`Landing page success` : t`Landing page error`
}

function getTooltipDescription(resultSummary: { success: boolean }) {
  return resultSummary.success
    ? t`All of your checks passed correctly. Looks like you are good to go!`
    : t`It looks like some of your checks failed. You may want to contact your Site Owner to ensure your site is configured correctly.<p><em>Landing page overlays such as cookie acceptance can prevent tag validation from working properly.</em></p>`
}

function getSuccessMessage(success: boolean) {
  return success ? t`Success` : t`Failed`
}

function getSuccessIcon(success: boolean) {
  return success ? 'check_circle' : 'error'
}

function getSuccessTextClass(success: boolean) {
  return success ? 'text-success' : 'text-danger'
}

function getValidationName(validation: { validationTypeName: string }) {
  return validation.validationTypeName
}

//  TODO: remove/refactor to use only API
const OPTION_NAME_PLACEHOLDERS = {
  page_exists: msg`Page exists`,
  aa_pixel_exists: msg`Adobe Analytics Pixel exists`,
  aa_correct_report_suite: msg`Correct report suite`,
  aa_correct_report: msg`Correct report`,
  ga_pixel_exists: msg`Google Analytics Pixel exists`,
  ga_correct_property: msg`Correct property`,
  fb_pixel_exists: msg`Facebook Pixel exists`,
  fb_pixel_correct_id: msg`Facebook Pixel correct ID`,
  ordmp_pixel_exists: msg`Oracle DMP pixel exists`,
  ordmp_correct_site_id: msg`Oracle DMP correct cite ID`,
  sfdmp_pixel_exists: msg`Salesforce DMP pixel exists`,
  sfdmp_correct_conf_id: msg`Salesforce DMP Correct Conf ID`,
  aau_pixel_exists: msg`Adobe Audience Manager Pixel exists`,
  aau_correct_source_id: msg`Adobe Audience Manager correct source ID`,
  gads_pixel_exists: msg`Google AdSense Pixel exists`,
  responsys_pixel_exists: msg`Responsys Pixel exists`,
  al_pixel_exists: msg`Adobe Launch Pixel exists`,
  marketo_pixel_exists: msg`Marketo Pixel exists`,
  et_pixel_exists: msg`ExactTarget Pixel exists`,
  adtm_pixel_exists: msg`Adobe DTM exists`,
  at_pixel_exists: msg`Adobe target exists`,
} as const satisfies Record<string, MessageDescriptor>
const OPTION_NAMES: Record<string, string> = {}

function getOptionName(option: { optionSlug: string }) {
  if (
    !OPTION_NAMES[option.optionSlug] &&
    typeof OPTION_NAME_PLACEHOLDERS[option.optionSlug] !== 'undefined'
  ) {
    OPTION_NAMES[option.optionSlug] = i18n._(OPTION_NAME_PLACEHOLDERS[option.optionSlug])
  }
  return OPTION_NAMES[option.optionSlug]
}

function renderToTd(td: HTMLTableColElement, htmlString: string) {
  const sanitized = sanitizeHtmlAndSvg(htmlString)
  td.innerHTML = sanitized
}

const auth = storeState.Auth

if (auth.user?.id) {
  // WTF is this?
  void (async () => {
    const { data } = await api.get<{ slug: string; name: string }[]>(`/api/v1/validations/types`)
    data.forEach(type => {
      OPTION_NAMES[type.slug] = type.name
    })
  })()
}
